/** The /insights inbox API. An insight is one agent finding addressed by
 * a stable fingerprint (panel/slice/phenomenon) so re-runs upsert instead
 * of re-announcing. Email replies form an append-only conversation.
 * Triage is the team's shared done/snoozed/dismissed state and changes
 * only through the inbox controls. */
import { apiFetch } from '../auth/apiClient'
import type { AttachmentMeta } from '../components/attachments/AttachmentCards'

export const INSIGHTS_CHANGED_EVENT = 'progetto-arca:insights-changed'

export type Severity = 'high' | 'medium' | 'low'

export interface Triage {
  state: 'done' | 'snoozed' | 'dismissed'
  note: string
  until: string | null
  actor: string
  at: string
}

export interface InsightMessage {
  body: string
  actor: string
  source: 'email' | 'app' | 'agent' | 'voice'
  createdAt: string
  attachments?: AttachmentMeta[]
}

export interface Insight {
  fingerprint: string
  kind?: 'finding' | 'question'
  origin?: 'user' | 'agent'
  title: string
  severity: Severity
  panel: string
  window: string
  computedAt: string
  body: string[]
  numbers: { label: string; value: string }[]
  action: string
  messages: InsightMessage[]
  readAt: string | null
  triage: Triage | null
  attachments?: AttachmentMeta[]
}

export interface BriefingItem {
  rank: number
  whyNow: string
  fingerprint: string
  title: string
  severity: Severity
  summary: string[]
  suggestedAction: string
  updatedAt: string | null
}

export interface InboxBriefing {
  items: BriefingItem[]
  reviewedAt: string
  complete: boolean
}

async function json<T>(response: Response): Promise<T> {
  if (!response.ok) throw new Error(`Insights API failed (${response.status})`)
  return (await response.json()) as T
}

async function fetchInsightsRequest(signal?: AbortSignal): Promise<Insight[]> {
  const response = await apiFetch('/api/internal/insights', { signal })
  return (await json<{ insights: Insight[] }>(response)).insights
}

export function fetchInsights(): Promise<Insight[]> {
  return fetchInsightsRequest()
}

export function fetchInsightsWithSignal(signal: AbortSignal): Promise<Insight[]> {
  return fetchInsightsRequest(signal)
}

export async function fetchBriefing(): Promise<InboxBriefing | null> {
  const response = await apiFetch('/api/internal/briefing')
  return (await json<{ briefing: InboxBriefing | null }>(response)).briefing
}

export async function fetchAskAddress(): Promise<string | null> {
  const response = await apiFetch('/api/internal/insights/ask-address')
  return (await json<{ address: string | null }>(response)).address
}

export async function markInsightRead(fingerprint: string): Promise<string> {
  const response = await apiFetch(`/api/internal/insights/${fingerprint}/read`, { method: 'PUT' })
  const readAt = (await json<{ readAt: string }>(response)).readAt
  window.dispatchEvent(new Event(INSIGHTS_CHANGED_EVENT))
  return readAt
}

export async function replyToInsight(
  fingerprint: string,
  body: string,
  attachments: File[] = [],
): Promise<InsightMessage> {
  const form = new FormData()
  form.set('body', body)
  attachments.forEach((file) => form.append('attachments', file))
  const response = await apiFetch(`/api/internal/insights/${fingerprint}/messages`, {
    method: 'POST',
    body: form,
  })
  return (await json<{ message: InsightMessage }>(response)).message
}

export async function createInsightQuestion(
  title: string,
  body: string,
  attachments: File[] = [],
): Promise<Insight> {
  const form = new FormData()
  form.set('title', title)
  form.set('body', body)
  attachments.forEach((file) => form.append('attachments', file))
  const response = await apiFetch('/api/internal/insights/questions', {
    method: 'POST',
    body: form,
  })
  return (await json<{ insight: Insight }>(response)).insight
}

export async function setTriage(
  fingerprint: string,
  state: Triage['state'],
  note: string,
): Promise<Triage> {
  const response = await apiFetch(`/api/internal/insights/${fingerprint}/triage`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ state, note }),
  })
  return (await json<{ triage: Triage }>(response)).triage
}

export async function clearTriage(fingerprint: string): Promise<void> {
  await json(await apiFetch(`/api/internal/insights/${fingerprint}/triage`, { method: 'DELETE' }))
}

/** Email this finding to the signed-in user; resolves to the address it went to. */
export async function emailInsight(fingerprint: string): Promise<string> {
  const response = await apiFetch(`/api/internal/insights/${fingerprint}/email`, { method: 'POST' })
  if (response.status === 503) throw new Error('not-configured')
  return (await json<{ to: string }>(response)).to
}
